import { useQuery } from '@tanstack/react-query';
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Platform } from 'react-native';

import { VStack, theme } from '@squire/ui';

import { getShopNotificationConsentQuery } from '@/api/booking/getShopNotificationConsentQuery';
import {
  BottomSheet,
  type BottomSheetRef,
} from '@/components/_shared/BottomSheet';
import { ScreenHeader } from '@/components/_shared/ScreenHeader';
import { AdultOnlyConsent } from '@/components/booking/AdultOnlyConsent';
import { CancellationPolicyConsent } from '@/components/booking/CancellationPolicyConsent';
import { NotificationConsent } from '@/components/booking/NotificationConsent';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type {
  BookingConsentSheetProps,
  NotificationConsentChoice,
} from './types';

const BookingConsentSheet = forwardRef<
  BottomSheetRef,
  BookingConsentSheetProps
>(
  (
    {
      shopId,
      testID,
      onDismiss,
      onContinue,
      cart,
      shop,
      customer,
      alertsToShow = ['notification'],
      onNotificationConsentChange,
      ...props
    },
    ref,
  ) => {
    const innerSheetRef = useRef<BottomSheetRef>(null);
    const { t } = useScreenTranslations('NotificationConsent');
    const { t: tAdultOnly } = useScreenTranslations('AdultOnlyConsent');
    const { t: tCancellation } = useScreenTranslations(
      'CancellationPolicyConsent',
    );

    const [currentPageIndex, setCurrentPageindex] = useState(0);

    const { data: notificationsConsent } = useQuery(
      getShopNotificationConsentQuery({
        shopId: shopId || shop?.id,
        customer: customer || {},
      }),
    );

    const showAdultOnlyConsent =
      alertsToShow.includes('adultOnly') && shop?.adultsOnly;

    const showCancellationPolicyConsent =
      alertsToShow.includes('cancelationPolicy') &&
      !!cart?.bookingInfo?.canCancelBefore;

    // Show-if-unknown: mirrors water-on-web's getShouldShow*PromotionConsent()
    // (`?? true`). Each channel is shown unless the backend explicitly says the
    // prompt should not appear for it. While the consent query is loading the
    // sheet is not opened (it is opened imperatively after payment-add), so the
    // fallback only takes effect once `notificationsConsent` has resolved.
    const showSmsConsent =
      alertsToShow.includes('notification') &&
      (notificationsConsent?.showPrompt?.promotional?.phone ?? true);

    const showEmailConsent =
      alertsToShow.includes('notification') &&
      (notificationsConsent?.showPrompt?.promotional?.email ?? true);

    const pages = useMemo(() => {
      const result = [];
      if (showAdultOnlyConsent) result.push('adultOnly');
      if (showCancellationPolicyConsent) result.push('cancelationPolicy');
      if (showSmsConsent) result.push('notificationSms');
      if (showEmailConsent) result.push('notificationEmail');

      return result;
    }, [
      showAdultOnlyConsent,
      showCancellationPolicyConsent,
      showSmsConsent,
      showEmailConsent,
    ]);

    // Clamp the index in case `pages` shrinks while the sheet is open (e.g. the
    // consent query resolves mid-session and removes a channel), so we never
    // land on an undefined page and render nothing.
    const safePageIndex = Math.min(currentPageIndex, pages.length - 1);
    const currentPage = pages[safePageIndex];

    // The sheet uses dynamic sizing (no explicit snapPoints), so it auto-fits
    // its height to whichever card is currently rendered. No manual snapTo is
    // needed — and calling snapTo(1) here crashes, since the only snap point is
    // index 0.

    const resetPageIndex = () => {
      onDismiss?.();
      setCurrentPageindex(0);
    };

    const goToNextPage = (consent?: NotificationConsentChoice) => {
      if (consent) {
        onNotificationConsentChange?.(consent);
      }

      if (currentPageIndex >= pages.length - 1) {
        onContinue?.();
        innerSheetRef.current?.close();
        return;
      }

      setCurrentPageindex((index) => index + 1);
    };

    useImperativeHandle(ref, () => ({
      open: () => {
        if (pages.length === 0) {
          onContinue?.();
          return;
        }
        innerSheetRef.current?.open();
      },
      close: () => innerSheetRef.current?.close(),
      snapTo: (index: number) => innerSheetRef.current?.snapTo(index),
      snapToIndex: (index: number) => innerSheetRef.current?.snapToIndex(index),
    }));

    const handleClose = () => {
      innerSheetRef.current?.close();
    };

    const isNotificationPage =
      currentPage === 'notificationSms' || currentPage === 'notificationEmail';

    const consentType = isNotificationPage ? 'notification' : 'adultOnly';

    const sheetLabel = isNotificationPage
      ? t('title', { name: shop?.name })
      : currentPage === 'adultOnly'
        ? tAdultOnly('title')
        : tCancellation('title');

    styles.useVariants({ consentType });

    // Render only the active page. Each consent card fills the sheet and the
    // user advances through them one at a time via goToNextPage (mirrors
    // water-on-web). Rendering all cards in a horizontal pager broke the sheet
    // layout on web, where the pager did not clip to a single page.
    return (
      <BottomSheet
        {...props}
        testID={testID}
        accessibilityLabel={sheetLabel}
        hideHandle
        dismissible={false}
        scrollable={Platform.OS !== 'web'}
        onDismiss={resetPageIndex}
        ref={innerSheetRef}
      >
        {/* AdultOnlyConsent renders its own ScreenHeader (title + close), so
              the sheet-level header is only shown for the other pages. The
              notification pages put the consent title in the header. */}
        {currentPage !== 'adultOnly' && (
          <ScreenHeader
            title={isNotificationPage ? t('title', { name: shop?.name }) : ''}
            onClose={handleClose}
            paddingVertical={theme.spacing[6]}
            paddingHorizontal={theme.spacing[5]}
          />
        )}
        <VStack centerVertical style={styles.content}>
          {currentPage === 'adultOnly' && (
            <AdultOnlyConsent
              style={styles.pageContainer}
              onAgree={goToNextPage}
              onClose={handleClose}
            />
          )}
          {currentPage === 'cancelationPolicy' && (
            <CancellationPolicyConsent
              style={styles.pageContainer}
              shopTimezone={shop?.timezone}
              canCancelBefore={cart?.bookingInfo?.canCancelBefore}
              onAgree={goToNextPage}
            />
          )}
          {currentPage === 'notificationSms' && (
            <NotificationConsent
              channel="sms"
              style={styles.pageContainer}
              onAgree={() => goToNextPage({ phone: true })}
              onSkip={() => goToNextPage({ phone: false })}
            />
          )}
          {currentPage === 'notificationEmail' && (
            <NotificationConsent
              channel="email"
              style={styles.pageContainer}
              onAgree={() => goToNextPage({ email: true })}
              onSkip={() => goToNextPage({ email: false })}
            />
          )}
        </VStack>
      </BottomSheet>
    );
  },
);

BookingConsentSheet.displayName = 'BookingConsentSheet';

export { BookingConsentSheet };
